import get from 'lodash.get';
import { buildUrlQueryParams } from '@/utilities';

import JwtBaseClient from './JwtBaseClient';

/**
 * @class CapitalClient
 *
 * @description API wrapper for Capital API
 */
class CapitalClient {
  constructor({ apiVersion } = {}) {
    const apiVersionPath = (apiVersion && apiVersion !== 1) ? `v${apiVersion}/` : '';
    this.jwtBaseClient = new JwtBaseClient(
      `${import.meta.env.VITE_CAPITAL_GATEWAY_BASE_URI}api/${apiVersionPath}`,
      'response.data',
    );
  }

  /**
   * @function fetchInvoiceForAuction
   * @description Return an invoice for a given auction that was paid with ACV Capital
   * @param {Number} auctionId
   * @returns {Promise}
   */
  fetchInvoiceForAuction(auctionId) {
    return this.jwtBaseClient
      .get(`invoice/auction/${auctionId}`)
      .then((response) => get(response, 'data'))
      .catch((err) => {
        throw err;
      });
  }

  /**
   * @function fetchCapitalFees
   * @description Return an array of fee options
   * @param {Number} auctionTotal
   * @returns {Promise}
   */
  fetchCapitalFees(auctionTotal, dealerId) {
    return this.jwtBaseClient
      .get(`invoice/fee/${auctionTotal}?dealer_id=${dealerId}`)
      .then((response) => get(response, 'data'))
      .catch((err) => err.message);
  }

  /**
   * Posts a payment for an invoice
   * @param auctionId The id of the auction to pay off.
   * @param total The total to pay off.
   * @param fromPortfolio Indicates the payment data is coming from the OI table.
   * @returns {Promise<AxiosResponse<any>>}
   */
  postInvoicePayment(auctionId, total, fromPortfolio = false) {
    return this.jwtBaseClient
      .post(buildUrlQueryParams('payment', [['from_invoice_table', fromPortfolio]]), { auction_id: auctionId, total })
      .then((response) => get(response, 'data'));
  }

  /**
   * Post a payment for multiple invoices.
   * @param invoices - an array of objects with auctionId and total per invoice
   * @param [fromPortfolio=false] - boolean
   * @returns {Promise<AxiosResponse<any>>}
   */
  postMultipleInvoicesPayment(invoices, fromPortfolio = false) {
    return this.jwtBaseClient
      .post(
        buildUrlQueryParams('payments', [['from_invoice_table', fromPortfolio]]),
        invoices.map((invoice) => ({ auction_id: invoice.auctionId, total: invoice.total })),
      )
      .then((response) => get(response, 'data'));
  }

  /**
   * return an object with the capital dealer information.
   * @returns {Promise}
   */
  isUserCapital() {
    return this.jwtBaseClient
      .get('account/user/exists')
      .then((response) => get(response, 'data'));
  }

  /**
   * Posts a new account lead
   * @param source platform environment. Eg. web.
   * @param body includes:
   * dealerId:The id of the auction to pay off.
   * auctionId: The total to pay off.
   * campaign_name: The name of campaign
   * lead_source: Origin of lead generation
   * product_of_interest: Selected product
   * @returns {Promise<AxiosResponse<any>>}
   */
  postNewAccountLead(source, body) {
    let url = 'account/lead';
    if (source) {
      url += `?source=${source}`;
    }
    return this.jwtBaseClient
      .post(url, body)
      .then((response) => get(response, 'data'))
      .catch((err) => {
        throw err;
      });
  }

  /**
   * @function fetchDealerDetails
   * @description Returns an object with dealer credit and transaction data
   * @param {Number} dealerId
   * @returns {Promise}
   */
  fetchDealerDetails(dealerId) {
    return this.jwtBaseClient
      .get(`account/details/dealer/${dealerId}`)
      .then((response) => get(response, 'data'))
      .catch((err) => err.message.error);
  }

  /**
   * @function fetchOpenInvoices
   * @description This function fetches open invoices for a given dealer ID.
   * @param dealerId - The ID of the dealer you want to fetch invoices for.
   * @returns {Promise}
   */
  fetchOpenInvoices(dealerId) {
    return this.jwtBaseClient
      .get(`v1/open-invoices/dealer/${dealerId}`)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function fetchCheckoutLeadGenData
  * @description Returns an object with checkout Lead Gen's data of a dealer
  * @param {Number} dealerId
  * @returns {promise}
  */
  fetchCheckoutLeadGenData(dealerId) {
    return this.jwtBaseClient
      .get(`lead-generation/dealer/${dealerId}/source/CHECKOUT_SOURCE/content`)
      .then((response) => get(response, 'data'))
      .catch((err) => err?.message?.error);
  }

  /**
  * @function engageNewLeadGen
  * @description Returns an object with checkout Lead Gen's data of a dealer
  * @param version version of the endpoint allow 1 or 2
  * @param body includes:
  * dealer_id: The id of the auction to pay off.
  * content_slug: Slug of the product.
  * request_source: Origin of lead generation.
  * send_lead_to_sf: Boolean to send or not the data to Salesforce.
  * lead_data
    * includes:
      * dealerId:The id of the auction to pay off.
      * auctionId: The total to pay off. (optional).
      * campaign_name: The name of campaign.
      * lead_source: Origin of lead generation.
      * product_of_interest: Selected product.
  * @returns {Promise<AxiosResponse<any>>}
  */
  engageNewLeadGen(body, version = 1) {
    return this.jwtBaseClient
      .post(`lead-generation/content/engagement?version=${version}`, body)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function getSalesRep
  * @description Returns an object with Sales rep data
  * @param {Number} dealerId
  * @returns {promise}
  */
  getSalesRep(dealerId) {
    return this.jwtBaseClient
      .get(`lead-generation/lead/sales-rep/${dealerId}`)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function getFeedbackStatusDetail
  * @description Return an object with the form application status
  * @param {Number} dealerId
  * @returns {Promise}
  */
  getFeedbackStatusDetail(dealerId) {
    return this.jwtBaseClient
      .get(`lead-generation/dealer/${dealerId}/application-form/details`)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function getCapitalFees
  * @description Return an object with the fees information of an specific dealer.
  * @param {Number} dealerId
  * @returns {Promise}
  */
  getCapitalFees(dealerId, isV2) {
    const url = isV2
      ? `v2/fee/dealer/${dealerId}`
      : `fee/dealer/${dealerId}`;
    return this.jwtBaseClient
      .get(url)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function getFeesNotification
  * @description Return an object with a message and a boolean to indicate if
  * the fees notification should be shown on capital portal.
  * @param {Number} dealerId
  * @returns {Promise}
  */
  getFeesNotification(dealerId) {
    return this.jwtBaseClient
      .get(`portal/dealer/${dealerId}/start-up-notification`)
      .then((response) => get(response, 'data'));
  }

  /**
   * It makes a GET request to the API endpoint `title/document/{auctionId}`
   * and returns the response data
   * @param auctionId - The id of the auction you want to get the title image for.
   * @returns The title image for the auction.
   */
  getTitleImage(auctionId) {
    return this.jwtBaseClient
      .downloadFile(`v1/title/document/${auctionId}`, null, { accept: 'application/pdf' });
  }

  /**
   * Returns detailed information from an specific auction.
   * @param auctionId - The id of the auction you want to get details for.
   * @returns The response from the API call.
   */
  getAuctionDetails(auctionId) {
    return this.jwtBaseClient
      .get(`v1/auction/details/${auctionId}`)
      .then((response) => get(response, 'data'));
  }

  /**
   * It returns the bill of sale for the auction with the given auctionId
   * @param auctionId - The id of the auction you want to get the bill of sale for.
   * @returns The bill of sale for the auction.
   */
  downloadAuctionBillOfSale(auctionId) {
    return this.jwtBaseClient.downloadFile(
      `v1/bill_of_sale/${auctionId}`,
      null,
      { accept: 'application/pdf' },
    );
  }

  /**
  * @function checkOutOfRange
  * @description Return an object with information about if the dealer is
  * out of range or not.
  * @param {Number} dealerId
  * @param {Number} amount
  * @returns {Promise}
  */
  checkOutOfRange(dealerId, amount) {
    return this.jwtBaseClient
      .get(`fee/out-of-range/dealer/${dealerId}/amount/${amount}`)
      .then((response) => get(response, 'data'));
  }

  /**
   * @function checkEligibility
   * @description Return an object with information about if the dealer is out of range or not.
   * @param {Number} dealerId - The dealer ID
   * @param {Number} [amount=0] - The amount of the loan
   * @param {Number} [extraFees=0] - The amount of extra fees that the dealer has to pay.
   * @param {Number} [pendingBalance=0] - Amount of money the dealer has already paid to the company
   * @returns {Promise}
   */
  checkEligibility({
    dealerId,
    amount = 0,
    extraFees = 0,
    pendingBalance = 0,
    auctionId,
  } = {}) {
    return this.jwtBaseClient
      .get(buildUrlQueryParams(`fee/eligibility/dealer/${dealerId}`, {
        amount, extra_fees: extraFees, pending_balance: pendingBalance, auction_id: auctionId,
      }))
      .then((response) => get(response, 'data'));
  }

  /**
  * @function getDealerLandingPageData
  * @description Return an object with all the information about landing page
  * for only one dealer.
  * @param {Number} dealerId
  * @returns {Promise}
  */
  getDealerLandingPageData(dealerId) {
    return this.jwtBaseClient
      .get(`lead-generation/dealer/${dealerId}/landing-page`)
      .then((response) => get(response, 'data'));
  }

  /**
   * @function getDealerOnboardingTasks
   * @description Return an object indicating the status of each onboarding
   * task for a specific dealer ID.
   * @param {Number} dealerId
   * @returns {Promise}
   */
  getDealerOnboardingTasks(dealerId) {
    return this.jwtBaseClient
      .get(`onboarding/${dealerId}/tasks`)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function storeLineIncrease
  * @param dealerId: ID of the selected dealer
  * @param auctionId: ID of the current auction
  * @param product: dealer's Capital product
  * @returns {Promise<AxiosResponse<any>>}
  */
  storeLineIncrease(dealerId, auctionId, product) {
    const url = `account/dealer/${dealerId}/request-line-increase`;
    return this.jwtBaseClient
      .post(`${url}?auction_id=${auctionId}&product=${product}`)
      .then((response) => get(response, 'data'));
  }

  /**
  * @function fetchClosedInvoices
  * @description Return an object with all the information about closed invoices
  * for a given dealer.
   * @param dealerId - The dealer ID of the dealer you want to fetch invoices for.
   * @param [startDate=null] - The date to start fetching invoices from.
   * @param [endDate=null] - The date to fetch invoices up to.
   * @returns {Promise}
   */
  fetchClosedInvoices(dealerId, startDate = null, endDate = null) {
    const params = [
      ...(startDate ? [['start_date', startDate]] : []),
      ...(endDate ? [['end_date', endDate]] : []),
    ];

    return this.jwtBaseClient
      .get(buildUrlQueryParams(`closed-invoices/${dealerId}`, params))
      .then((response) => get(response, 'data'));
  }
}

export default CapitalClient;
